import { Ban, Check, Clock, FilePen, FileSignature, PenLine, Stethoscope } from 'lucide-react';
import {
  EPISODE_STATUS_LABELS,
  EpisodeTransitions,
  type EpisodeStatus,
} from '@datazo/contracts';
import { cn } from '@/shared/lib/cn';

/**
 * Stepper visual del progreso del episodio clínico.
 *
 * El flujo lineal tiene 5 hitos:
 *   abierto → consentimiento → anestesia → gasto → cerrado
 *
 * El estado `anulado` es **off-flow** — cuando un episodio se anula desde
 * cualquier punto, mostramos el stepper congelado en el hito hasta donde
 * llegó + un banner aparte indicando la anulación. Por eso el stepper NO
 * incluye `anulado` como nodo.
 */

const FLOW_ORDER: ReadonlyArray<EpisodeStatus> = [
  'abierto',
  'consentimiento',
  'anestesia',
  'gasto',
  'cerrado',
];

const STEP_ICON: Record<EpisodeStatus, JSX.Element> = {
  abierto: <FilePen className="w-3.5 h-3.5" />,
  consentimiento: <PenLine className="w-3.5 h-3.5" />,
  anestesia: <Stethoscope className="w-3.5 h-3.5" />,
  gasto: <FileSignature className="w-3.5 h-3.5" />,
  cerrado: <Check className="w-3.5 h-3.5" />,
  anulado: <Ban className="w-3.5 h-3.5" />,
};

interface EpisodeStepperProps {
  current: EpisodeStatus;
  /** Si se pasa, cada hito alcanzable vía `EpisodeTransitions[current]` es clickable. */
  onTransition?(to: EpisodeStatus): void;
  disabled?: boolean;
  className?: string;
  /**
   * Hitos a omitir del stepper porque el procedimiento no los requiere
   * (ej. consultas y manometrías no necesitan `anestesia` ni `gasto`).
   * Si el episodio actual está en uno de los hitos omitidos, igualmente se
   * muestra (no se puede ocultar un estado en el que estás), pero los hitos
   * NO actuales sí se filtran.
   */
  skip?: ReadonlyArray<EpisodeStatus>;
}

export function EpisodeStepper({
  current,
  onTransition,
  disabled,
  className,
  skip,
}: EpisodeStepperProps): JSX.Element {
  // Si el episodio está anulado, mostramos una vista alternativa.
  if (current === 'anulado') {
    return <CancelledStepper className={className} />;
  }

  const skipSet = new Set<EpisodeStatus>(skip ?? []);
  // Mantenemos siempre el estado actual visible aunque esté en `skip` —
  // ocultar el hito en el que estás sería desconcertante.
  const visibleFlow = FLOW_ORDER.filter((s) => !skipSet.has(s) || s === current);
  const fullIdx = FLOW_ORDER.indexOf(current);
  const allowedNext = (EpisodeTransitions[current] ?? []).filter((s) => !skipSet.has(s));

  return (
    <div className={cn('w-full', className)}>
      <div className="flex items-center gap-1.5">
        {visibleFlow.map((status, idx) => {
          const fullStatusIdx = FLOW_ORDER.indexOf(status);
          const isDone = fullStatusIdx < fullIdx;
          const isCurrent = status === current;
          const isFuture = fullStatusIdx > fullIdx;
          const isReachable =
            allowedNext.includes(status) && onTransition !== undefined && !disabled;

          return (
            <FlowStep
              key={status}
              status={status}
              isFirst={idx === 0}
              isDone={isDone}
              isCurrent={isCurrent}
              isFuture={isFuture}
              isReachable={isReachable}
              onClick={isReachable ? () => onTransition(status) : undefined}
            />
          );
        })}
      </div>

      {/* Etiquetas debajo */}
      <div
        className="grid gap-1.5 mt-2"
        style={{ gridTemplateColumns: `repeat(${visibleFlow.length}, minmax(0, 1fr))` }}
      >
        {visibleFlow.map((status) => {
          const fullStatusIdx = FLOW_ORDER.indexOf(status);
          return (
            <div
              key={status}
              className={cn(
                'text-[10px] text-center leading-tight',
                status === current
                  ? 'text-text font-semibold'
                  : fullStatusIdx < fullIdx
                    ? 'text-success'
                    : 'text-text-muted',
              )}
            >
              {EPISODE_STATUS_LABELS[status]}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Internal                                                                   */
/* -------------------------------------------------------------------------- */

interface FlowStepProps {
  status: EpisodeStatus;
  isFirst: boolean;
  isDone: boolean;
  isCurrent: boolean;
  isFuture: boolean;
  isReachable: boolean;
  onClick?(): void;
}

function FlowStep({
  status,
  isFirst,
  isDone,
  isCurrent,
  isFuture,
  isReachable,
  onClick,
}: FlowStepProps): JSX.Element {
  return (
    <>
      {!isFirst && (
        <div
          className={cn(
            'flex-1 h-0.5 rounded-full',
            isDone || isCurrent ? 'bg-success' : 'bg-border',
          )}
        />
      )}
      <button
        type="button"
        onClick={onClick}
        disabled={onClick === undefined}
        title={
          isReachable
            ? `Avanzar a ${EPISODE_STATUS_LABELS[status]}`
            : isCurrent
              ? `Estado actual: ${EPISODE_STATUS_LABELS[status]}`
              : isDone
                ? `Completado: ${EPISODE_STATUS_LABELS[status]}`
                : `Pendiente: ${EPISODE_STATUS_LABELS[status]}`
        }
        className={cn(
          'relative w-9 h-9 rounded-full grid place-items-center flex-shrink-0 transition-all',
          'border-2',
          isDone &&
            'bg-success text-white border-success shadow-sm cursor-default',
          isCurrent &&
            'bg-primary text-white border-primary shadow-md ring-4 ring-primary/15 cursor-default',
          isFuture &&
            !isReachable &&
            'bg-surface-alt text-text-muted border-border cursor-not-allowed',
          isFuture &&
            isReachable &&
            'bg-surface text-primary border-primary/40 hover:bg-primary hover:text-white hover:border-primary cursor-pointer animate-pulse-soft',
        )}
      >
        {isDone ? <Check className="w-4 h-4" /> : STEP_ICON[status]}
        {isCurrent && (
          <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-success border-2 border-surface" />
        )}
      </button>
    </>
  );
}

function CancelledStepper({ className }: { className?: string }): JSX.Element {
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-md bg-danger-soft border border-red-200 text-red-800 text-sm',
        className,
      )}
    >
      <div className="w-9 h-9 rounded-full bg-danger text-white grid place-items-center flex-shrink-0">
        <Ban className="w-4 h-4" />
      </div>
      <div className="flex-1">
        <div className="font-semibold">Episodio anulado</div>
        <div className="text-xs text-red-700">
          El flujo clínico fue interrumpido. Revisa el motivo en los detalles del episodio.
        </div>
      </div>
      <Clock className="w-4 h-4 text-red-700 flex-shrink-0" />
    </div>
  );
}
